import { useState, useEffect, useRef } from 'react';
import { Segment, SegmentBox, SegmentSeparator, focusSegment, padSegment, parseTwoDigitSegment } from './SegmentedInput';

function splitValue(value) {
  if (!value) return { monthStr: '', dayStr: '', yearStr: '' };
  const [y, m, d] = value.split('-');
  return { monthStr: m ?? '', dayStr: d ?? '', yearStr: y ?? '' };
}

function DatePicker({ value, onChange, error }) {
  const [monthStr, setMonthStr] = useState('');
  const [dayStr, setDayStr] = useState('');
  const [yearStr, setYearStr] = useState('');
  const [internalError, setInternalError] = useState(false);
  const [focusedSeg, setFocusedSeg] = useState(null);
  const containerRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const yearRef = useRef(null);

  useEffect(() => {
    const { monthStr, dayStr, yearStr } = splitValue(value);
    setMonthStr(monthStr);
    setDayStr(dayStr);
    setYearStr(yearStr);
    setInternalError(false);
  }, [value]);

  const handleMonthChange = e => {
    const { value: v, complete } = parseTwoDigitSegment(e.target.value, 12);
    setMonthStr(v);
    if (complete) focusSegment(dayRef);
  };

  const handleDayChange = e => {
    const { value: v, complete } = parseTwoDigitSegment(e.target.value, 31);
    setDayStr(v);
    if (complete) focusSegment(yearRef);
  };

  const handleYearChange = e => {
    setYearStr(e.target.value.replace(/\D/g, '').slice(0, 4));
  };

  const backspaceTo = (currentValue, prevRef) => e => {
    if (e.key === 'Backspace' && currentValue === '') focusSegment(prevRef);
  };

  const handleContainerClick = e => {
    if (e.target.tagName === 'INPUT') return;
    if (monthStr.length < 2) focusSegment(monthRef);
    else if (dayStr.length < 2) focusSegment(dayRef);
    else focusSegment(yearRef);
  };

  const handleContainerBlur = e => {
    if (containerRef.current && containerRef.current.contains(e.relatedTarget)) return;

    if (monthStr === '' && dayStr === '' && yearStr === '') {
      setInternalError(false);
      if (value !== null) onChange(null);
      return;
    }
    if (monthStr === '' || dayStr === '' || yearStr.length !== 4) {
      setInternalError(true);
      return;
    }

    const mm = padSegment(monthStr, 1, 12);
    const dd = padSegment(dayStr, 1, 31);
    const newValue = `${yearStr}-${mm}-${dd}`;
    setMonthStr(mm);
    setDayStr(dd);
    setInternalError(false);
    if (newValue !== value) onChange(newValue);
  };

  const segmentProps = name => ({
    isFocused: focusedSeg === name,
    onFocus: () => setFocusedSeg(name),
    onBlur: () => setFocusedSeg(null),
  });

  return (
    <SegmentBox
      ref={containerRef}
      onBlur={handleContainerBlur}
      onClick={handleContainerClick}
      error={internalError ? 'Enter a complete date (MM, DD, and YYYY)' : error}
    >
      <Segment ref={monthRef} value={monthStr} onChange={handleMonthChange} {...segmentProps('month')} />
      <SegmentSeparator>/</SegmentSeparator>
      <Segment
        ref={dayRef}
        value={dayStr}
        onChange={handleDayChange}
        onKeyDown={backspaceTo(dayStr, monthRef)}
        {...segmentProps('day')}
      />
      <SegmentSeparator>/</SegmentSeparator>
      <Segment
        ref={yearRef}
        maxLength={4}
        width="w-10"
        value={yearStr}
        onChange={handleYearChange}
        onKeyDown={backspaceTo(yearStr, dayRef)}
        {...segmentProps('year')}
      />
    </SegmentBox>
  );
}

export default DatePicker;
